import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCalendar, FiFileText, FiHome, FiUser } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { useTrailNavigate } from '../../context/EntityTrailContext';
import { toast } from '../../toasts';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import MessageBox from '../../components/MessageBox';
import ContractService from '../../services/contract.service';
import BillService from '../../services/bill.service';
import { getError } from '../../utils';
import ContractDebts from '../Contracts/ContractDebts';
import ContractInfo from '../Contracts/ContractInfo';
import AssetBills from '../Assets/AssetBills';
import { splitBillsByType } from '../Assets/billGrouping';
import ContractActivity from './ContractActivity';
import Balance from '../Balance/Balance';
import { ContractDeletionModal } from '../../components/CascadeDeletionModal';
import CloseContractModal from '../../components/CloseContractModal/CloseContractModal';
import SingleObjectDocumentManager from '../../components/SingleObjectDocumentManager/SingleObjectDocumentManager';
import GenerateContractModal from '../../components/GenerateContractModal/GenerateContractModal';
import CommissionList from '../Commissions/CommissionList';
import { isBalanceEnabled, isCommissionsEnabled } from '../../utils/featureFlags';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import { EntityList, DetailHeader, DetailBreadcrumb, TabNav, Button } from '../../components/EntityList';

const emptyTabState = { rows: [], loading: true, error: '' };

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function isVigente(contract) {
	return contract.status !== 'closed' && !(contract.periodEnd && moment.utc(contract.periodEnd).isBefore(moment.utc()));
}

export default function ContractDetail() {
	const { t } = useLabels();
	const params = useParams();
	const navigate = useNavigate();
	const navigateWithTrail = useTrailNavigate();
	const { canEdit, canCreate, canDelete } = usePermissions();

	const { id, tab } = params;

	const [contract, setContract] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	const [showCloseModal, setShowCloseModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showGeneratePdf, setShowGeneratePdf] = useState(false);

	const [debtsState, setDebtsState] = useState(emptyTabState);
	const [billsState, setBillsState] = useState(emptyTabState);

	// A contract has no bills of its own — they belong to the rented asset. The
	// id only exists once the contract has loaded, hence the separate effect.
	const assetId = contract.property?._id || contract.property || null;

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			const result = await ContractService.getDetails(id);
			setContract(result.data);
			setError('');
		} catch (err) {
			setError(getError(err));
			toast.error(t('msg.rentalLoadFailed'));
		} finally {
			setLoading(false);
		}
	}, [id]);

	const fetchDebts = useCallback(async () => {
		setDebtsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await ContractService.getDebtsForContract(id);
			setDebtsState({ rows: data, loading: false, error: '' });
		} catch (err) {
			setDebtsState({ rows: [], loading: false, error: getError(err) });
		}
	}, [id]);

	const fetchBills = useCallback(async () => {
		if (!assetId) return;
		setBillsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await BillService.listBillsForAsset(assetId);
			setBillsState({ rows: data, loading: false, error: '' });
		} catch (err) {
			setBillsState({ rows: [], loading: false, error: getError(err) });
		}
	}, [assetId]);

	const billsByType = useMemo(() => splitBillsByType(billsState.rows), [billsState.rows]);

	useEffect(() => {
		fetchData();
		fetchDebts();
	}, [id]);

	useEffect(() => {
		fetchBills();
	}, [fetchBills]);

	const balanceEnabled = isBalanceEnabled();
	const commissionsEnabled = isCommissionsEnabled();
	const hasBillsTabs = Boolean(assetId);
	const TAB_SLUGS = [
		'info',
		'cobros',
		...(hasBillsTabs ? ['bills-eventual', 'bills-periodic'] : []),
		...(balanceEnabled ? ['balance'] : []),
		...(commissionsEnabled ? ['commissions'] : []),
		'documents',
		'activity',
	];
	const currentTab = TAB_SLUGS.includes(tab) ? tab : 'info';

	useEffect(() => {
		if (!loading && tab !== currentTab) {
			navigate(`/AdminScreen/contracts/contract/${id}/${currentTab}`, { replace: true });
		}
	}, [loading, tab, currentTab, id]);

	const tabs = [
		{ id: 'info', label: t('title.details') },
		{ id: 'cobros', label: t('title.collections'), count: debtsState.rows.length },
		...(hasBillsTabs ? [
			{ id: 'bills-eventual', label: t('title.billsEventual'), count: billsByType.eventual.length },
			{ id: 'bills-periodic', label: t('title.billsPeriodic'), count: billsByType.periodic.length },
		] : []),
		...(balanceEnabled ? [{ id: 'balance', label: t('title.balance') }] : []),
		...(commissionsEnabled ? [{ id: 'commissions', label: t('title.commissions') }] : []),
		{ id: 'documents', label: t('title.documents') },
		{ id: 'activity', label: t('title.activity') },
	];

	const newContractHandler = () => {
		navigate('/AdminScreen/newContract', { state: { currentContract: contract } });
	};



	const isClosed = contract.status === 'closed';

	const subline = [
		contract.owner?.name && { label: t('entity.tenant'), icon: <FiUser />, value: contract.owner.name, strong: true },
		contract.amount && {
			label: t('field.amount'),
			icon: <FiFileText />,
			value: <span className='tw:font-medium tw:text-cat-2'>{contract.currency} {Number(contract.amount).toLocaleString('es-UY')}</span>,
		},
		(contract.periodInit || contract.periodEnd) && {
			label: t('field.periodInit'),
			icon: <FiCalendar />,
			value: `${formatDate(contract.periodInit)} → ${formatDate(contract.periodEnd)}`,
		},
		contract.property?.owner?.name && {
			label: t('entity.assetOwner'),
			value: (
				<span
					className='tw:cursor-pointer tw:text-ink-2 tw:hover:text-ink-0'
					onClick={() => navigateWithTrail(`/AdminScreen/accounts/account/${contract.property.owner._id}`)}
				>
					{contract.property.owner.name}
				</span>
			),
		},
	].filter(Boolean);

	return loading ? (
		<EntityList>
			<DetailPageLoader entityLabel={t('entity.contract')} />
		</EntityList>
	) : error ? (
		<MessageBox variant='danger'>{error}</MessageBox>
	) : (
		<EntityList>
			<DetailBreadcrumb
				parentLabel={t('entity.contract.plural')}
				parentTo='/AdminScreen/contracts'
				current={contract.customId}
			/>

			<DetailHeader
				name={contract.property?.name || contract.customId || ''}
				kicker={`${t('entity.contract')} · ${contract.customId || ''}`}
				statusLabel={isClosed ? 'Finalizado' : isVigente(contract) ? 'Vigente' : 'Vencido'}
				statusTone={isClosed ? 'neutral' : isVigente(contract) ? 'ok' : 'danger'}
				subline={subline}
				avatar={
					<span className='tw:flex tw:h-[72px] tw:w-[72px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[14px] tw:bg-cat-2 tw:text-white tw:shadow-sm'>
						<FiHome size={28} />
					</span>
				}
				actions={
					<>
						{!isClosed && canCreate() && (
							<Button onClick={() => navigate(`/AdminScreen/contracts/contract/${id}/newContractPayment?returnTo=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`)}>
								{t('action.addCollection')}
							</Button>
						)}
						<Button onClick={() => setShowGeneratePdf(true)}>{t('action.generateContract')}</Button>
						{!isClosed && canEdit() && <Button onClick={() => setShowCloseModal(true)}>{`Finalizar ${t('entity.contract')}`}</Button>}
						{isClosed && canCreate() && <Button onClick={newContractHandler}>{`Nuevo ${t('entity.contract')} a partir del actual`}</Button>}
						{!isClosed && canDelete() && <Button onClick={() => setShowDeleteModal(true)}>{t('action.delete')}</Button>}
						{!isClosed && canEdit() && (
							<Button variant='primary' onClick={() => navigate(`/AdminScreen/contracts/editContract/${id}`)}>
								{t('action.edit')}
							</Button>
						)}
					</>
				}
			/>

			<div className='tw:mt-5'>
				<TabNav tabs={tabs} current={currentTab} onChange={(nextTab) => navigate(`/AdminScreen/contracts/contract/${id}/${nextTab}`)} />
			</div>

			<div className='tw:mt-6'>
				{currentTab === 'info' && <ContractInfo contract={contract} />}
				{currentTab === 'cobros' && (
					<ContractDebts contract={contract} {...debtsState} onRefetch={fetchDebts} />
				)}
				{currentTab === 'bills-eventual' && hasBillsTabs && (
					<AssetBills
						asset={contract.property}
						billType='eventual'
						returnToBase={`/AdminScreen/contracts/contract/${id}`}
						{...billsState}
						onRefetch={fetchBills}
					/>
				)}
				{currentTab === 'bills-periodic' && hasBillsTabs && (
					<AssetBills
						asset={contract.property}
						billType='periodic'
						returnToBase={`/AdminScreen/contracts/contract/${id}`}
						{...billsState}
						onRefetch={fetchBills}
					/>
				)}
				{currentTab === 'balance' && balanceEnabled && <Balance type='contract' data={contract} />}
				{currentTab === 'commissions' && commissionsEnabled && (
					<CommissionList contractId={id} parentAmount={contract.amount} parentCurrency={contract.currency} />
				)}
				{currentTab === 'documents' && (
					<SingleObjectDocumentManager objectId={id} objectType='contract' documentType='contract' />
				)}
				{currentTab === 'activity' && <ContractActivity contractId={contract._id || id} />}
			</div>

			{showGeneratePdf && (
				<GenerateContractModal show={showGeneratePdf} onHide={() => setShowGeneratePdf(false)} entityType='contract' entityId={id} />
			)}

			<CloseContractModal
				show={showCloseModal}
				contract={contract}
				onClose={() => setShowCloseModal(false)}
				onClosed={() => {
					setShowCloseModal(false);
					fetchData();
				}}
			/>

			<ContractDeletionModal
				show={showDeleteModal}
				record={{ _id: id }}
				onClose={() => setShowDeleteModal(false)}
				onDeleted={() => navigate('/AdminScreen/contracts')}
			/>

		</EntityList>
	);
}
